import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Language, 
  Translations, 
  translations, 
  getTranslation, 
  STORAGE_KEY, 
  detectBrowserLanguage 
} from '@/lib/i18n';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  translations: Translations;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Try to get from localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'pt-br') {
        return stored;
      }
      // No stored preference, detect from browser language
      return detectBrowserLanguage();
    }
    return 'pt-br';
  });

  // Persist language to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    // Update html lang attribute
    document.documentElement.lang = language === 'pt-br' ? 'pt-BR' : 'en';
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const currentTranslations = useMemo(() => translations[language], [language]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      return getTranslation(currentTranslations, key, params);
    },
    [currentTranslations]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      translations: currentTranslations,
    }),
    [language, setLanguage, t, currentTranslations]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
